/**
 * <guarantee-celebration data-unlocked="true|false" data-badge="URL">
 *
 * Lives inside the cart drawer, which re-renders on every cart change. Because the drawer
 * MORPHS its DOM, we watch both connectedCallback and the data-unlocked attribute, and fire
 * the celebration modal once — at the moment the cart flips from locked to unlocked live.
 *
 * Drop this file in assets/ and load it from sections/cart-drawer.liquid.
 */

const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Module-scoped state — survives the cart drawer's in-place re-renders, resets on a
// full page load. This is what makes the modal fire ONLY on a live locked -> unlocked
// transition, and stay silent for a returning shopper whose saved cart is already unlocked.
let initialized = false;
let lastUnlocked = false;

// ---------------------------------------------------------------------------
// Delegated Add-to-cart listener for .cart-suggest__add-btn
//
// This MUST live here, not as an inline <script> inside cart-drawer-summary.liquid.
// That snippet is re-rendered by Shopify's Section Rendering API via DOM morphing
// (innerHTML-style), and inline <script> tags never execute when inserted that way —
// only a real page navigation runs them. That's why the button worked only after a
// full refresh: the refresh was the one moment the inline script actually ran.
//
// This file loads once via a real <script type="module"> tag in cart-drawer.liquid,
// which always executes on first load. The listener is bound to `document` (event
// delegation), so it keeps working no matter how many times the drawer morphs later —
// there's nothing here that needs to "re-run" per render.
// ---------------------------------------------------------------------------
document.addEventListener("click", (e) => {
  const btn = e.target.closest(".cart-suggest__add-btn");
  if (!btn || btn.disabled) return;
  e.preventDefault();

  const id = btn.getAttribute("data-variant-id");
  if (!id) return;

  // Use the SAME id cart-items-component compares against (this.sectionId),
  // read fresh from the DOM each click since the drawer may have re-rendered.
  const cartItems = document.querySelector("cart-items-component");
  const realSectionId = cartItems ? cartItems.dataset.sectionId : null;

  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Adding…";

  const sectionsToRequest = realSectionId ? [realSectionId] : [];

  fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      items: [{ id: Number(id), quantity: 1 }],
      sections: sectionsToRequest,
    }),
  })
    .then((r) => r.json())
    .then((data) => {
      // Matches ThemeEvents.cartUpdate ("cart:update") — the exact event
      // cart-items.js listens for and reads event.detail.data.sections from.
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            sourceId: "cart-suggest",
            data: {
              source: "cart-suggest",
              itemCount: 1,
              isIncremental: true,
              sections: data.sections || {},
            },
          },
        })
      );

      btn.textContent = "Added ✓";
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1500);
    })
    .catch(() => {
      btn.textContent = "Try again";
      btn.disabled = false;
    });
});

class GuaranteeCelebration extends HTMLElement {
  // The cart drawer re-renders by MORPHING (same reason the theme's product-recommendations
  // uses a MutationObserver): morphing preserves this element and only mutates its
  // data-unlocked attribute, so connectedCallback fires just once. We therefore also watch
  // the attribute and funnel both paths into one evaluator — robust to morph OR replace.
  static get observedAttributes() { return ["data-unlocked"]; }

  connectedCallback() {
    this.#evaluate(this.dataset.unlocked === "true");
    this.#repaintBadge();
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === "data-unlocked") {
      this.#evaluate(newValue === "true");
      this.#repaintBadge();
    }
  }

  /**
   * Safari/WebKit repaint bug: the badge's `filter` (grayscale <-> color) is toggled
   * by a CSS class that changes because the drawer MORPHS the DOM — it's an attribute
   * patch, not a live user interaction. Chrome repaints eagerly either way; Safari's
   * compositor sometimes leaves the promoted layer (transform: translateZ(0)) stale
   * until some other event forces a paint — which is why dragging the image "fixes" it.
   * We force that paint ourselves right after the class flips.
   */
  #repaintBadge() {
    requestAnimationFrame(() => {
      const img = document.querySelector(".cart-suggest__badge img");
      if (!img) return;
      const prev = img.style.opacity;
      // Nudge a style Safari must repaint for, then release it next frame.
      img.style.opacity = "0.9999";
      void img.offsetHeight; // force synchronous reflow
      requestAnimationFrame(() => {
        img.style.opacity = prev;
      });
    });
  }

  #evaluate(unlocked) {
    // Never auto-fire inside the theme editor.
    if (window.Shopify && Shopify.designMode) return;

    // First reading this page-load: set the baseline and do nothing.
    // A returning shopper already holding camera + bag stops here — no surprise pop-up.
    if (!initialized) {
      initialized = true;
      lastUnlocked = unlocked;
      return;
    }

    // Celebrate only on a live locked -> unlocked flip in front of the shopper.
    if (unlocked && !lastUnlocked) {
      setTimeout(() => this.#open(this.dataset.badge || ""), 250);
    }
    lastUnlocked = unlocked;
  }

  #open(badgeUrl) {
    if (document.getElementById("kmto-celebrate")) return; // singleton

    const dlg = document.createElement("dialog");
    dlg.id = "kmto-celebrate";
    dlg.innerHTML = this.#markup(badgeUrl);
    document.body.appendChild(dlg);

    // showModal() places this dialog in the browser's top layer, on top of any
    // other top-layer element (like the theme's own <dialog> cart drawer),
    // because the most recently opened top-layer element stacks highest.
    dlg.showModal();

    const close = () => {
      dlg.close();
      window.removeEventListener("keydown", onKey);
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    dlg.addEventListener("close", () => dlg.remove(), { once: true });
    dlg.querySelectorAll("[data-close]").forEach((b) => (b.onclick = close));
    dlg.querySelector(".kmto__backdrop").onclick = close;

    dlg.querySelector(".kmto__modal").focus();
    if (!REDUCED) this.#confetti(dlg.querySelector("canvas"));
  }

  #markup(badge) {
    const badgeImg = badge
      ? `<img src="${badge}" alt="100-Day Happiness Guarantee">`
      : "";
    return `
      <div class="kmto__backdrop"></div>
      <canvas class="kmto__confetti" aria-hidden="true"></canvas>
      <div class="kmto__modal" role="document" tabindex="-1">
        <div class="kmto__top">
          <button class="kmto__x" data-close aria-label="Close">&times;</button>
          <div class="kmto__rays" aria-hidden="true"></div>
          <div class="kmto__badge">${badgeImg}</div>
          <div class="kmto__eyebrow">Guarantee unlocked</div>
          <h2 class="kmto__title">100 days of happiness,<br>now yours</h2>
        </div>
        <div class="kmto__body">
          <p class="kmto__lede">Your order now includes our <b>100-Day Happiness Guarantee</b> — a hundred whole days to unwrap it, play, and fall in love. If it isn&rsquo;t the magic you hoped for, we&rsquo;ll make it right.</p>
          <div class="kmto__perks">
            <div class="kmto__perk-row"><i>&#10003;</i><span><b>100 days</b> to fall in love, risk-free</span></div>
            <div class="kmto__perk-row"><i>&#10003;</i><span>Full <b>refund or replacement</b>, your choice</span></div>
            <div class="kmto__perk-row"><i>&#10003;</i><span>No fuss &mdash; <b>we handle everything</b></span></div>
          </div>
          <button class="kmto__cta" data-close>Great!!!</button>
        </div>
      </div>
      ${this.#styles()}`;
  }

  #confetti(cv) {
    const ctx = cv.getContext("2d");
    const COLORS = ["#e07a2c", "#f4c15f", "#12925a", "#e79bb4", "#fff3e0", "#d8563f"];
    const size = () => { cv.width = window.innerWidth; cv.height = window.innerHeight; };
    size();
    const cx = cv.width / 2, cy = cv.height * 0.32;
    const parts = [];
    for (let i = 0; i < 150; i++) {
      const a = Math.random() * Math.PI * 2, s = 4 + Math.random() * 9;
      parts.push({ x: cx, y: cy, vx: Math.cos(a) * s, vy: Math.sin(a) * s - 4,
        g: 0.16 + Math.random() * 0.12, w: 6 + Math.random() * 6, h: 8 + Math.random() * 8,
        rot: Math.random() * 6, vr: -0.3 + Math.random() * 0.6, c: COLORS[i % COLORS.length],
        life: 0, max: 90 + Math.random() * 50 });
    }
    const tick = () => {
      ctx.clearRect(0, 0, cv.width, cv.height);
      let alive = false;
      for (const p of parts) {
        p.life++; if (p.life > p.max) continue; alive = true;
        p.vy += p.g; p.x += p.vx; p.y += p.vy; p.rot += p.vr; p.vx *= 0.99;
        ctx.save(); ctx.globalAlpha = Math.max(0, 1 - p.life / p.max);
        ctx.translate(p.x, p.y); ctx.rotate(p.rot); ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); ctx.restore();
      }
      if (alive) requestAnimationFrame(tick); else ctx.clearRect(0, 0, cv.width, cv.height);
    };
    tick();
  }

  #styles() {
    return `<style>
      #kmto-celebrate{
        border:none;padding:0;margin:auto;background:transparent;
        max-width:460px;width:calc(100% - 40px);max-height:calc(100% - 40px);
        overflow:visible;font-family:inherit;
      }
      #kmto-celebrate::backdrop{background:rgba(30,26,22,.55)}
      #kmto-celebrate .kmto__backdrop{position:absolute;inset:-9999px;z-index:-1}
      #kmto-celebrate .kmto__confetti{position:fixed;inset:0;pointer-events:none;z-index:2}
      #kmto-celebrate .kmto__modal{position:relative;z-index:3;width:100%;background:#fff;border-radius:22px;overflow:hidden;box-shadow:0 30px 70px rgba(0,0,0,.35);outline:none;animation:kmtoRise .5s cubic-bezier(.2,.9,.3,1) both}
      #kmto-celebrate .kmto__top{position:relative;text-align:center;padding:30px 26px 20px;background:radial-gradient(120% 100% at 50% 0%,#fff3e0 0%,#fbeada 55%,#fff 100%)}
      #kmto-celebrate .kmto__x{position:absolute;top:14px;right:14px;z-index:5;width:32px;height:32px;border:none;border-radius:50%;background:rgba(255,255,255,.7);font-size:18px;line-height:1;color:#6c6560;cursor:pointer}
      #kmto-celebrate .kmto__x:hover{background:#fff}
      #kmto-celebrate .kmto__rays{position:absolute;left:50%;top:96px;width:300px;height:300px;transform:translate(-50%,-50%);opacity:.3;pointer-events:none;filter:blur(3px)}
      #kmto-celebrate .kmto__rays::before{content:"";position:absolute;inset:0;background:repeating-conic-gradient(from 0deg,#f0b25e 0deg 2deg,transparent 2deg 15deg) center/100% 100%;-webkit-mask:radial-gradient(circle at center,transparent 58px,#000 80px,#000 120px,transparent 150px);mask:radial-gradient(circle at center,transparent 58px,#000 80px,#000 120px,transparent 150px);animation:kmtoSpin 22s linear infinite}
      #kmto-celebrate .kmto__badge{width:132px;height:132px;margin:0 auto 4px;position:relative;z-index:2;filter:drop-shadow(0 8px 18px rgba(190,95,30,.35));animation:kmtoStamp .7s cubic-bezier(.2,1.4,.4,1) .15s both}
      #kmto-celebrate .kmto__badge img{width:100%;height:100%;object-fit:contain}
      #kmto-celebrate .kmto__eyebrow{font-size:12px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#e07a2c;margin-top:8px;animation:kmtoFade .4s ease .5s both}
      #kmto-celebrate .kmto__title{font-family:Fraunces,Georgia,serif;font-weight:600;font-size:27px;line-height:1.12;margin:7px 0 0;animation:kmtoFade .5s ease .6s both}
      #kmto-celebrate .kmto__body{padding:6px 26px 24px;text-align:center}
      #kmto-celebrate .kmto__lede{font-size:14.5px;line-height:1.55;color:#4a4540;margin:12px 4px 18px;animation:kmtoFade .5s ease .7s both}
      #kmto-celebrate .kmto__lede b{color:#1d1b19;font-weight:600}
      #kmto-celebrate .kmto__perks{text-align:left;margin-bottom:22px;background:#fbf7f0;border:1px solid #ece7df;border-radius:12px;padding:2px 13px;animation:kmtoFade .5s ease .82s both}
      #kmto-celebrate .kmto__perk-row{display:flex;align-items:center;gap:11px;padding:9px 0;font-size:13px;font-weight:500}
      #kmto-celebrate .kmto__perk-row+.kmto__perk-row{border-top:1px solid #ece7df}
      #kmto-celebrate .kmto__perk-row i{width:22px;height:22px;border-radius:50%;background:#12925a;color:#fff;flex:none;display:grid;place-items:center;font-size:12px;font-style:normal}
      #kmto-celebrate .kmto__cta{display:block;width:100%;box-sizing:border-box;border:none;border-radius:99px;background:#f4c15f;color:#3a2c10;font-family:var(--font-heading--family,Fraunces,Georgia,serif);font-weight:700;font-size:19px;padding:14px;cursor:pointer;text-align:center;animation:kmtoFade .5s ease .92s both}
      #kmto-celebrate .kmto__cta:hover{background:#eab646}
      @keyframes kmtoFade{from{opacity:0}to{opacity:1}}
      @keyframes kmtoRise{from{opacity:0;transform:translateY(26px) scale(.96)}to{opacity:1;transform:none}}
      @keyframes kmtoStamp{0%{opacity:0;transform:scale(.2) rotate(-28deg)}55%{opacity:1;transform:scale(1.12) rotate(6deg)}100%{transform:scale(1) rotate(0)}}
      @keyframes kmtoSpin{to{transform:rotate(360deg)}}
      @media (prefers-reduced-motion:reduce){#kmto-celebrate *{animation:none!important}#kmto-celebrate .kmto__rays{display:none}}
    </style>`;
  }
}

if (!customElements.get("guarantee-celebration")) {
  customElements.define("guarantee-celebration", GuaranteeCelebration);
}